import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { RoadDamagesStrapiPanel } from './RoadDamagesStrapiPanel';
import { WorkReportsList } from './WorkReportsList';
import { Header } from '../header/header';
import './ProjectView.css';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface Project {
  id: number;
  attributes: {
    name: string;
    description?: string;
    status: 'planning' | 'in_progress' | 'completed' | 'paused';
    location?: string;
    client?: string;
    projectManager?: string;
    budget?: number;
    startDate?: string;
    endDate?: string;
    image?: {
      data?: {
        attributes: {
          url: string;
        };
      };
    };
    alerts?: {
      data: any[];
    };
    workReports?: {
      data: any[];
    };
  };
}

export const ProjectView = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'damages' | 'workReports'>('overview');

  useEffect(() => {
    if (id) {
      fetchProject(id);
    }
  }, [id]);

  const fetchProject = async (projectId: string) => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      const response = await fetch(`${backendUrl}/api/projects/${projectId}?populate=*`);
      const data = await response.json();
      setProject(data.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'planning': return '#FF9800';
      case 'paused': return '#9E9E9E';
      default: return '#666';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('completed');
      case 'in_progress': return t('inProgress');
      case 'planning': return t('planning');
      case 'paused': return t('paused');
      default: return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const roadDamageCount = project?.attributes.alerts?.data?.filter(
    (alert: any) => alert.attributes?.roadDamageNumber
  ).length || 0;

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  if (!project) {
    return <div className="project-not-found">{t('projectNotFound')}</div>;
  }

  return (
    <div className="project-view">
      <Header />

      <div className="project-content">
        <div className="project-header">
          <Link to="/" className="back-button">
            <ArrowBackIcon />
            {t('backToDashboard')}
          </Link>

          <div className="project-title-section">
            <h1>{project.attributes.name}</h1>
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(project.attributes.status) }}
            >
              {getStatusText(project.attributes.status)}
            </span>
          </div>
        </div>

        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('overview')}
          </button>
          <button
            className={`tab-button ${activeTab === 'damages' ? 'active' : ''}`}
            onClick={() => setActiveTab('damages')}
          >
            {t('damages')} ({roadDamageCount})
          </button>
          <button
            className={`tab-button ${activeTab === 'workReports' ? 'active' : ''}`}
            onClick={() => setActiveTab('workReports')}
          >
            {t('workReports')} ({project.attributes.workReports?.data?.length || 0})
          </button>
        </div>

        <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="project-details-grid">
              <div className="project-image-section">
                {project.attributes.image?.data?.attributes.url ? (
                  <img
                    src={`${sessionStorage.getItem('backendUrl')}${project.attributes.image.data.attributes.url}`}
                    alt={project.attributes.name}
                    className="project-main-image"
                  />
                ) : (
                  <div className="placeholder-main-image">
                    <span>{project.attributes.name.charAt(0).toUpperCase()}</span>
                  </div>
                )}
              </div>

              <div className="project-info-section">
                <div className="info-card">
                  <h3>{t('projectInformation')}</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t('description')}:</label>
                      <span>{project.attributes.description || t('noDescription')}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('location')}:</label>
                      <span>{project.attributes.location || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('client')}:</label>
                      <span>{project.attributes.client || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('projectManager')}:</label>
                      <span>{project.attributes.projectManager || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('budget')}:</label>
                      <span>{formatCurrency(project.attributes.budget)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('startDate')}:</label>
                      <span>{formatDate(project.attributes.startDate)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('endDate')}:</label>
                      <span>{formatDate(project.attributes.endDate)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="project-stats-grid">
              <div className="stat-card">
                <h3>{roadDamageCount}</h3>
                <p>{t('totalDamages')}</p>
              </div>
              <div className="stat-card">
                <h3>{project.attributes.workReports?.data?.length || 0}</h3>
                <p>{t('workReports')}</p>
              </div>
              <div className="stat-card">
                <h3>
                  {project.attributes.alerts?.data?.filter(alert =>
                    alert.attributes?.roadDamageNumber &&
                    new Date(alert.attributes.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  ).length || 0}
                </h3>
                <p>{t('recentDamages')}</p>
              </div>
              <div className="stat-card">
                <h3>
                  {project.attributes.workReports?.data?.filter((report: any) =>
                    report.attributes.status === 'completed'
                  ).length || 0}
                </h3>
                <p>{t('completedWorkReports')}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'damages' && (
          <div className="damages-tab">
            <RoadDamagesStrapiPanel
              projectId={parseInt(id!)}
              projectName={project.attributes.name}
            />
          </div>
        )}

        {activeTab === 'workReports' && (
          <div className="work-reports-tab">
            <WorkReportsList projectId={parseInt(id!)} />
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
